// src/pages/Notificacoes/RelatorioEmail.tsx

import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button,
  Form, Spinner, Modal
} from 'react-bootstrap';
import {
  FaEnvelope, FaFilter, FaDownload,
  FaSearch, FaTimes, FaCheckCircle,
  FaTimesCircle, FaExclamationCircle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { emailNotificationService } from '../../services/notificationService';
import { NotificationTable } from '../../components/NotificationTable';
import {
  EmailNotificationLog,
  NotificationType,
  NotificationStatus,
} from '../../types/notification.types';

// ─────────────────────────────────────────────
// Tipos internos
// ─────────────────────────────────────────────
interface Filters {
  startDate:  string;
  endDate:    string;
  customerId: string;
  type:       string;
  status:     string;
  searchTerm: string;
}

interface Stats {
  total:   number;
  sent:    number;
  failed:  number;
  pending: number;
}

const EMPTY_FILTERS: Filters = {
  startDate:  '',
  endDate:    '',
  customerId: '',
  type:       '',
  status:     '',
  searchTerm: '',
};

const EMPTY_STATS: Stats = { total: 0, sent: 0, failed: 0, pending: 0 };

const PAGE_SIZE = 20;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function buildFilterObj(filters: Filters) {
  const obj: Record<string, any> = {};
  if (filters.startDate)  obj.startDate  = new Date(filters.startDate);
  if (filters.endDate)    obj.endDate    = new Date(filters.endDate);
  if (filters.customerId) obj.customerId = filters.customerId;
  if (filters.type)       obj.type       = filters.type as NotificationType;
  if (filters.status)     obj.status     = filters.status as NotificationStatus;
  if (filters.searchTerm) obj.searchTerm = filters.searchTerm;
  return Object.keys(obj).length > 0 ? obj : undefined;
}

function formatDate(date: Date | string | null) {
  if (!date) return '-';
  return new Date(date).toLocaleString('pt-BR', {
    day:    '2-digit',
    month:  '2-digit',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

// ─────────────────────────────────────────────
// Stat Card
// ─────────────────────────────────────────────
interface StatCardProps {
  label:   string;
  value:   number;
  icon:    React.ReactNode;
  color:   string;
  loading: boolean;
}

function StatCard({ label, value, icon, color, loading }: StatCardProps) {
  return (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body className="d-flex align-items-center gap-3">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 48, height: 48, backgroundColor: `${color}1A` }}
        >
          <span style={{ color, fontSize: 20 }}>{icon}</span>
        </div>
        <div>
          <div className="text-muted small">{label}</div>
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <h4 className="mb-0 fw-bold" style={{ color }}>
              {value.toLocaleString('pt-BR')}
            </h4>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// ─────────────────────────────────────────────
// Modal de Detalhes
// ─────────────────────────────────────────────
interface DetailModalProps {
  show:    boolean;
  log:     EmailNotificationLog | null;
  onClose: () => void;
}

const TYPE_LABELS: Record<NotificationType, string> = {
  [NotificationType.BEFORE_DUE]:      'Antes do Vencimento',
  [NotificationType.DUE_TODAY]:       'Vence Hoje',
  [NotificationType.AFTER_DUE]:       'Após Vencimento',
  [NotificationType.PAYMENT_CONFIRM]: 'Confirmação de Pagamento',
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  SENT:    { label: '✓ Enviado',   color: '#198754' },
  FAILED:  { label: '✗ Falha',     color: '#dc3545' },
  PENDING: { label: '⏳ Pendente', color: '#ffc107' },
  BOUNCED: { label: '↩ Devolvido', color: '#6c757d' },
};

function DetailModal({ show, log, onClose }: DetailModalProps) {
  if (!log) return null;

  const statusInfo = STATUS_LABELS[log.status?.toUpperCase()] ?? {
    label: log.status,
    color: '#6c757d',
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="d-flex align-items-center gap-2 fs-6">
          <FaEnvelope className="text-primary" />
          Detalhes do E-mail
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="g-3 mb-3">
          <Col md={6}>
            <div className="p-3 rounded bg-light h-100">
              <div className="text-muted small mb-1">Cliente</div>
              <div className="fw-semibold">{log.customerName || '—'}</div>
            </div>
          </Col>

          <Col md={6}>
            <div className="p-3 rounded bg-light h-100">
              <div className="text-muted small mb-1">E-mail</div>
              <code className="fw-semibold">{log.customerEmail || '—'}</code>
            </div>
          </Col>

          <Col md={4}>
            <div className="p-3 rounded bg-light h-100">
              <div className="text-muted small mb-1">Tipo</div>
              <div className="fw-semibold">
                {log.type ? TYPE_LABELS[log.type] ?? log.type : '—'}
              </div>
            </div>
          </Col>

          <Col md={4}>
            <div className="p-3 rounded bg-light h-100">
              <div className="text-muted small mb-1">Status</div>
              <div className="fw-semibold" style={{ color: statusInfo.color }}>
                {statusInfo.label}
              </div>
            </div>
          </Col>

          <Col md={4}>
            <div className="p-3 rounded bg-light h-100">
              <div className="text-muted small mb-1">Enviado em</div>
              <div className="fw-semibold">{formatDate(log.sentAt)}</div>
            </div>
          </Col>
        </Row>

        {/* Assunto */}
        <div className="mb-3">
          <div className="text-muted small mb-1">Assunto</div>
          <div className="p-3 rounded border bg-white fw-semibold">
            {log.subject || '—'}
          </div>
        </div>

        {/* Corpo da mensagem */}
        <div className="mb-3">
          <div className="text-muted small mb-1">Mensagem</div>
          <div
            className="p-3 rounded border bg-white"
            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', maxHeight: 260, overflowY: 'auto' }}
          >
            {log.message || '—'}
          </div>
        </div>

        {/* Motivo da falha */}
        {log.failureReason && (
          <div className="p-3 rounded border border-danger bg-danger bg-opacity-10">
            <div className="text-danger small fw-semibold mb-1">Motivo da Falha</div>
            <div className="small text-danger">{log.failureReason}</div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─────────────────────────────────────────────
// Página principal
// ─────────────────────────────────────────────
export default function RelatorioEmail() {
  const [loading,       setLoading]       = useState(false);
  const [exporting,     setExporting]     = useState(false);
  const [data,          setData]          = useState<EmailNotificationLog[]>([]);
  const [stats,         setStats]         = useState<Stats>(EMPTY_STATS);
  const [page,          setPage]          = useState(1);
  const [totalPages,    setTotalPages]    = useState(0);
  const [filters,       setFilters]       = useState<Filters>(EMPTY_FILTERS);
  const [showFilters,   setShowFilters]   = useState(false);
  const [selectedLog,   setSelectedLog]   = useState<EmailNotificationLog | null>(null);
  const [showModal,     setShowModal]     = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // ── Fetch ───────────────────────────────────
  const fetchData = async (pageNum: number = 1, currentFilters: Filters = filters) => {
    try {
      setLoading(true);
      const filterObj = buildFilterObj(currentFilters);
      const response  = await emailNotificationService.getHistory(pageNum, PAGE_SIZE, filterObj);

      setData(response.data);
      setPage(pageNum);
      setTotalPages(response.totalPages ?? Math.ceil(response.total / PAGE_SIZE));

      setStats({
        total:   response.total,
        sent:    (response as any).totalSent    ?? response.data.filter(d => d.status === NotificationStatus.SENT).length,
        failed:  (response as any).totalFailed  ?? response.data.filter(d => d.status === NotificationStatus.FAILED).length,
        pending: (response as any).totalPending ?? response.data.filter(d => d.status === NotificationStatus.PENDING).length,
      });
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico de e-mails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  // ── Filtros ─────────────────────────────────
  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setShowFilters(false);
    fetchData(1, filters);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    fetchData(1, EMPTY_FILTERS);
  };

  // ── Export ──────────────────────────────────
  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await emailNotificationService.exportCSV(buildFilterObj(filters));
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = `emails-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Arquivo exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar dados');
    } finally {
      setExporting(false);
    }
  };

  // ── Delete ──────────────────────────────────
  const handleDelete = async (id: string) => {
    try {
      await emailNotificationService.delete(id);
      toast.success('Registro deletado');
      fetchData(page);
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar registro');
    }
  };

  // ── View / Modal ────────────────────────────
  const handleView = async (id: string) => {
    try {
      setLoadingDetail(true);
      setShowModal(true);
      const details = await emailNotificationService.getDetails(id);
      setSelectedLog(details);
    } catch (error) {
      console.error('Erro ao buscar detalhes:', error);
      toast.error('Erro ao carregar detalhes');
      setShowModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <Container fluid className="p-4">

      {/* ── Cabeçalho ── */}
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h1 className="mb-0 d-flex align-items-center gap-2 fs-4 fw-bold">
            <FaEnvelope className="text-primary" />
            Relatório de E-mails
          </h1>
          <small className="text-muted">
            Histórico de notificações por e-mail enviadas
          </small>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant={showFilters ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setShowFilters(p => !p)}
          >
            <FaFilter className="me-2" />
            Filtros
            {hasActiveFilters && (
              <span className="ms-2 badge bg-warning text-dark">!</span>
            )}
          </Button>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleExport}
            disabled={exporting || data.length === 0}
          >
            {exporting
              ? <Spinner size="sm" animation="border" className="me-2" />
              : <FaDownload className="me-2" />
            }
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </Button>
        </div>
      </div>

      {/* ── Stats ── */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <StatCard label="Total de Registros" value={stats.total}   icon={<FaEnvelope />}          color="#0d6efd" loading={loading} />
        </Col>
        <Col xs={6} md={3}>
          <StatCard label="Enviados"            value={stats.sent}    icon={<FaCheckCircle />}       color="#198754" loading={loading} />
        </Col>
        <Col xs={6} md={3}>
          <StatCard label="Pendentes"           value={stats.pending} icon={<FaExclamationCircle />} color="#ffc107" loading={loading} />
        </Col>
        <Col xs={6} md={3}>
          <StatCard label="Falhas"              value={stats.failed}  icon={<FaTimesCircle />}       color="#dc3545" loading={loading} />
        </Col>
      </Row>

      {/* ── Painel de Filtros ── */}
      {showFilters && (
        <Card className="mb-4 shadow-sm border-primary border-opacity-25">
          <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2">
            <span className="fw-semibold small d-flex align-items-center gap-2">
              <FaFilter className="text-primary" />
              Filtros
            </span>
            <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setShowFilters(false)}>
              <FaTimes />
            </Button>
          </Card.Header>

          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Data Inicial</Form.Label>
                  <Form.Control type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Data Final</Form.Label>
                  <Form.Control type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Tipo</Form.Label>
                  <Form.Select name="type" value={filters.type} onChange={handleFilterChange}>
                    <option value="">Todos</option>
                    <option value={NotificationType.BEFORE_DUE}>Antes do Vencimento</option>
                    <option value={NotificationType.DUE_TODAY}>Vence Hoje</option>
                    <option value={NotificationType.AFTER_DUE}>Após Vencimento</option>
                    <option value={NotificationType.PAYMENT_CONFIRM}>Confirmação de Pagamento</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Status</Form.Label>
                  <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                    <option value="">Todos</option>
                    <option value={NotificationStatus.SENT}>Enviado</option>
                    <option value={NotificationStatus.PENDING}>Pendente</option>
                    <option value={NotificationStatus.FAILED}>Falha</option>
                    <option value={NotificationStatus.BOUNCED}>Devolvido</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Buscar</Form.Label>
                  <Form.Control
                    type="text"
                    name="searchTerm"
                    placeholder="Nome ou e-mail..."
                    value={filters.searchTerm}
                    onChange={handleFilterChange}
                    onKeyDown={e => e.key === 'Enter' && handleApplyFilters()}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-3">
              <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                <FaSearch className="me-2" />
                Aplicar
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
              >
                <FaTimes className="me-2" />
                Limpar Filtros
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ── Tabela ── */}
      <NotificationTable
        loading={loading}
        data={data}
        channel="EMAIL"
        columns={['Cliente', 'E-mail', 'Tipo', 'Assunto', 'Status', 'Data']}
        onView={handleView}
        onDelete={handleDelete}
        onExport={handleExport}
        emptyMessage={
          hasActiveFilters
            ? 'Nenhum registro encontrado para os filtros aplicados.'
            : 'Nenhum e-mail encontrado. As notificações aparecerão aqui após o primeiro envio.'
        }
      />

      {/* ── Paginação ── */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => fetchData(page - 1)}
            disabled={page === 1 || loading}
          >
            ← Anterior
          </Button>

          <span className="small text-muted">
            Página <strong>{page}</strong> de <strong>{totalPages}</strong>
          </span>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => fetchData(page + 1)}
            disabled={page === totalPages || loading}
          >
            Próxima →
          </Button>
        </div>
      )}

      {/* ── Modal de Detalhes ── */}
      {loadingDetail ? (
        <Modal show centered>
          <Modal.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted small">Carregando detalhes...</p>
          </Modal.Body>
        </Modal>
      ) : (
        <DetailModal
          show={showModal}
          log={selectedLog}
          onClose={() => { setShowModal(false); setSelectedLog(null); }}
        />
      )}

    </Container>
  );
}
